import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { ArrowRightLeft, ChevronRight, MapPin } from "lucide-react"
import type { LucideIcon } from "lucide-react"
import { StageChip } from "@/components/StageChip"
import type { AssetEvent } from "@/types/inventory"

interface EventMeta {
  icon: LucideIcon
  color: string
}

const EVENT_META: Record<AssetEvent["event_type"], EventMeta> = {
  stage_change: { icon: ArrowRightLeft, color: "#7E22CE" },
  location_change: { icon: MapPin, color: "#0891B2" },
}

function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function formatMonth(d: Date): string {
  return capitalize(d.toLocaleDateString("pt-BR", { month: "long", year: "numeric" }))
}

function formatDate(d: Date): string {
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" })
}

function formatTime(d: Date): string {
  return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
}

interface AssetTimelineProps {
  events: AssetEvent[]
}

export function AssetTimeline({ events }: AssetTimelineProps) {
  const { t } = useTranslation()
  const [filter, setFilter] = useState<"all" | AssetEvent["event_type"]>("all")

  const kinds = useMemo(
    () => Array.from(new Set(events.map((e) => e.event_type))),
    [events]
  )

  const filtered = useMemo(
    () => (filter === "all" ? events : events.filter((e) => e.event_type === filter)),
    [events, filter]
  )

  const groups = useMemo(() => {
    const map = new Map<string, AssetEvent[]>()
    filtered.forEach((e) => {
      const d = new Date(e.created_at)
      const key = formatMonth(d)
      if (!map.has(key)) map.set(key, [])
      map.get(key)!.push(e)
    })
    return Array.from(map.entries())
  }, [filtered])

  if (events.length === 0) {
    return (
      <p className="text-muted-foreground text-center py-8">
        {t("inventory.show.noEvents")}
      </p>
    )
  }

  return (
    <div>
      {/* Filter chips */}
      <div className="flex gap-1.5 flex-wrap mb-4 pb-3.5" style={{ borderBottom: "1px dashed var(--border)" }}>
        <FilterChip active={filter === "all"} onClick={() => setFilter("all")}>
          {t("inventory.events.filterAll", { defaultValue: "Tudo" })} · {events.length}
        </FilterChip>
        {kinds.map((k) => {
          const meta = EVENT_META[k]
          const count = events.filter((e) => e.event_type === k).length
          return (
            <FilterChip
              key={k}
              active={filter === k}
              color={meta.color}
              onClick={() => setFilter(k)}
            >
              <span
                className="w-[6px] h-[6px] rounded-full mr-1.5"
                style={{ background: meta.color }}
              />
              {t(`inventory.events.${k}`)} · {count}
            </FilterChip>
          )
        })}
      </div>

      <div className="relative">
        {/* vertical rail */}
        <div
          className="absolute top-1 bottom-1 w-[2px]"
          style={{ left: "15px", background: "var(--border)" }}
        />

        {groups.map(([month, evts], gi) => (
          <div key={month} className={gi < groups.length - 1 ? "mb-5" : ""}>
            {/* Month chapter marker */}
            <div className="relative pl-11 flex items-center gap-2.5 mb-2.5">
              <div
                className="absolute left-0 top-1/2 -translate-y-1/2 grid place-items-center w-8 h-8 rounded-full font-mono font-semibold"
                style={{
                  background: "var(--foreground)",
                  color: "var(--background)",
                  fontSize: "9px",
                  letterSpacing: "0.04em",
                  border: "3px solid var(--card)",
                  boxShadow: "0 0 0 1px var(--border)",
                }}
              >
                {month.split(" ")[0].slice(0, 3).toUpperCase()}
              </div>
              <div className="font-mono text-[10.5px] font-semibold uppercase text-muted-foreground tracking-[0.15em]">
                — {month}
              </div>
              <div className="flex-1 border-t border-dashed" style={{ borderColor: "var(--border)" }} />
            </div>

            {evts.map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

function EventCard({ event }: { event: AssetEvent }) {
  const { t } = useTranslation()
  const meta = EVENT_META[event.event_type]
  const Icon = meta.icon
  const d = new Date(event.created_at)
  const userName = event.user ? (event.user.name || event.user.email) : t("inventory.events.system")

  return (
    <div className="relative pl-11 mb-3">
      {/* Event dot on rail */}
      <div
        className="absolute left-1 top-3.5 grid place-items-center w-6 h-6 rounded-full z-[2]"
        style={{
          background: "var(--card)",
          border: `2px solid ${meta.color}`,
          boxShadow: "0 0 0 3px var(--card)",
        }}
      >
        <Icon className="w-3 h-3" style={{ color: meta.color }} />
      </div>

      <div
        className="rounded-md overflow-hidden"
        style={{ background: "var(--background)", border: "1px solid var(--border)" }}
      >
        {/* Card header: event label + timestamp */}
        <div
          className="flex justify-between items-center px-3 py-[7px] font-mono text-[10px] uppercase font-semibold tracking-[0.14em]"
          style={{
            background: `color-mix(in oklab, ${meta.color} 6%, transparent)`,
            borderBottom: "1px solid var(--border-soft)",
          }}
        >
          <span className="inline-flex items-center gap-1.5" style={{ color: meta.color }}>
            <span className="w-[5px] h-[5px] rounded-full" style={{ background: meta.color }} />
            {t(`inventory.events.${event.event_type}`)}
          </span>
          <span className="text-muted-foreground tracking-wide">
            {formatDate(d)} · {formatTime(d)}
          </span>
        </div>

        <div className="px-3.5 py-3">
          {event.event_type === "stage_change" && (event.from_stage || event.to_stage) && (
            <div className="flex items-center gap-2 flex-wrap mb-2.5">
              {event.from_stage && <StageChip stage={event.from_stage} />}
              {event.from_stage && event.to_stage && (
                <ChevronRight className="w-3 h-3 text-muted-foreground" />
              )}
              {event.to_stage && <StageChip stage={event.to_stage} />}
            </div>
          )}

          {event.event_type === "location_change" && (
            <div className="flex items-center gap-2 flex-wrap text-sm text-foreground font-medium mb-2.5">
              {event.from_location ? (
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
                  {event.from_location.name}
                </span>
              ) : (
                <span className="italic text-muted-foreground">
                  {t("inventory.events.fromNoLocation")}
                </span>
              )}
              <ChevronRight className="w-3 h-3 text-muted-foreground" />
              {event.to_location && (
                <span
                  className="inline-flex items-center gap-1.5 font-semibold"
                  style={{ color: meta.color }}
                >
                  <MapPin className="w-3.5 h-3.5" style={{ color: meta.color }} />
                  {event.to_location.name}
                </span>
              )}
            </div>
          )}

          {(event.product || event.customer || event.batch_number) && (
            <div className="flex gap-3.5 flex-wrap mt-1 text-[12.5px]">
              {event.product && (
                <div>
                  <span className="text-muted-foreground">{t("inventory.form.productLabel")}: </span>
                  <span className="font-semibold">{event.product.name}</span>
                </div>
              )}
              {event.customer && (
                <div>
                  <span className="text-muted-foreground">{t("inventory.form.customerLabel")}: </span>
                  <span className="font-semibold">{event.customer.name}</span>
                </div>
              )}
              {event.batch_number && (
                <div>
                  <span className="text-muted-foreground">{t("inventory.form.batchNumberLabel", { defaultValue: "Lote" })}: </span>
                  <span className="font-semibold font-mono">{event.batch_number}</span>
                </div>
              )}
            </div>
          )}

          {event.notes && (
            <div
              className="mt-2.5 px-3 py-2 text-[12.5px] italic text-muted-foreground rounded-r-md"
              style={{
                background: "var(--card)",
                borderLeft: `3px solid ${meta.color}`,
              }}
            >
              {event.notes}
            </div>
          )}

          {/* Footer: user */}
          <div
            className="mt-2.5 pt-2 flex items-center gap-2 text-xs text-muted-foreground"
            style={{ borderTop: "1px dashed var(--border-soft)" }}
          >
            <span
              className="grid place-items-center w-5 h-5 rounded-full font-bold"
              style={{
                fontSize: "10px",
                background: "color-mix(in oklab, var(--accent) 20%, transparent)",
                color: "var(--accent)",
              }}
            >
              {userName.charAt(0).toUpperCase()}
            </span>
            <span>
              {t("inventory.events.byUser", { defaultValue: "Por" })}{" "}
              <span className="text-foreground font-semibold">{userName}</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

interface FilterChipProps {
  active?: boolean
  color?: string
  onClick: () => void
  children: React.ReactNode
}

function FilterChip({ active, color, onClick, children }: FilterChipProps) {
  const bg = active ? (color ? `${color}15` : "var(--foreground)") : "var(--background)"
  const textColor = active ? (color || "var(--background)") : "var(--foreground)"
  const borderColor = active ? (color || "var(--foreground)") : "var(--border)"

  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center px-2.5 py-1 rounded-full text-[11.5px] font-medium transition-all"
      style={{
        background: bg,
        color: textColor,
        border: `1px solid ${borderColor}`,
      }}
    >
      {children}
    </button>
  )
}
